import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Moeda } from '../../../enums/moeda.enum';
import {
  moedasCompativeis,
  moedasCompativeisInterseccao,
  podeConverterEntre,
} from '../../../utils/moeda-pareamento.util';
import { arredondarParaMoeda } from '../../utils/arredondamento-moeda.util';
import { CotacoesDiretas, converterDireto } from '../../utils/moeda-conversao.util';

/**
 * Estrutura canonica de valores recebidos por moeda.
 *
 * Espelha as 4 chaves enviadas/lidas no DTO de pagamento (despesa, receita,
 * venda, transferencia, movimento de conta). Mantida em ordem fixa para
 * facilitar comparacao e merge controlado.
 */
export interface ValoresRecebidos {
  valorRecebidoEmGuarani: number;
  valorRecebidoEmReal: number;
  valorRecebidoEmDolar: number;
  valorRecebidoEmPeso: number;
}

/** Codigo ISO das 4 moedas operadas no PDV. */
export type MoedaCodigo = 'PYG' | 'USD' | 'BRL' | 'ARS';

/**
 * Configuracao calculada para renderizar o header de cada card.
 *
 * Implementa a "regra de ancora dinamica" (ver
 * `vendas-site/docs/ai/REGRA_ANCORA_MOEDA_FORMA_PAGAMENTO.md`):
 *
 *  - R1: a moeda da venda (`moedaPadrao`) aparece primeiro;
 *  - R2: o card-ancora exibe `taxaNumero = 1`;
 *  - R3: cards secundarios exibem a cotacao relativa a moeda da venda;
 *  - R4: o simbolo e o locale da taxa seguem a moeda do PROPRIO card
 *        (paridade com o `vendas-app`/mobile).
 *
 * O template usa `taxaNumero`, `taxaSimbolo`, `taxaDigits` e `taxaLocale`
 * para alimentar o pipe `moedaSimbolo` (CurrencyPipe wrapper).
 */
export interface CardConfig {
  /** Codigo ISO da moeda do card (define qual template renderizar). */
  moeda: MoedaCodigo;
  /** True se este card representa a propria moeda da venda. */
  ehAncora: boolean;
  /** Valor numerico exibido no header (1 para ancora, cotacao caso contrario). */
  taxaNumero: number;
  /** Codigo ISO usado pelo CurrencyPipe para o SIMBOLO do numero (= moeda do card). */
  taxaSimbolo: MoedaCodigo;
  /**
   * Formato de digitos para o CurrencyPipe, seguindo as casas decimais da
   * moeda do PROPRIO card (PYG=`1.0-0`, USD/BRL/ARS=`1.2-2`) — paridade com
   * o `vendas-app`/mobile.
   */
  taxaDigits: string;
  /** Locale do CurrencyPipe (`es-PY`, `pt-BR`, `en-US`, `es-AR`). */
  taxaLocale: string;
}

/**
 * Sub-componente reutilizavel que renderiza 1-4 cards de pagamento por moeda
 * (PYG/USD/BRL/ARS) com matriz de pareamento e formatacao consistente.
 *
 * Extraido do `app-forma-pagamento-dialog` (DRY) para que o dialog oficial e
 * outros formularios inline (despesa/receita) compartilhem o mesmo bloco
 * visual e mesma logica de gating.
 *
 * Visibilidade dos cards (modo Dinheiro):
 *  - Gate 1 (matriz do registro): apenas as moedas listadas em
 *    MOEDAS_COMPATIVEIS para `moedaPadrao`. Espelha
 *    `MoedaPareamentoValidator` no backend.
 *  - Gate 1b (matriz da empresa, opt-in): quando `moedaEmpresa` for
 *    informada (despesa/receita), a visibilidade passa a ser a INTERSECAO
 *    entre MOEDAS_COMPATIVEIS[moedaPadrao] e MOEDAS_COMPATIVEIS[moedaEmpresa].
 *    Cenarios cobertos:
 *      - Registro GUARANI + Empresa REAL -> {PYG, BRL, USD} (sem PESO)
 *      - Registro GUARANI + Empresa PESO -> {PYG, ARS}
 *      - Registro PESO    + Empresa REAL -> {PYG} (unica moeda em comum)
 *    Sem `moedaEmpresa`, o filtro reduz a apenas o Gate 1 (compatibilidade
 *    retroativa com consumidores existentes — PDV, fornecedor-compra, etc.).
 *  - Gate 2 (cotacao, defesa em profundidade): card ARS exige adicionalmente
 *    `cotPesoGuarani > 0`. Sem cotacao a conversao retorna zero e o card e
 *    ocultado para evitar inputs com calculos zerados.
 *
 * Edge case: `moedaPadrao` indefinida -> apenas card PYG (fallback historico).
 *
 * Limites do sub-componente (decisao D8):
 *  - NAO renderiza header de "total a pagar" / "total enviado" / hotkeys.
 *    Essas responsabilidades ficam no consumidor (dialog ou edit-form).
 *  - O consumidor recebe os valores via `(valoresChange)` e o convertido em
 *    moeda padrao via `(totalConvertidoChange)` para alimentar seus totais.
 */
@Component({
  selector: 'app-forma-pagamento-cards',
  templateUrl: './forma-pagamento-cards.component.html',
  styleUrls: ['./forma-pagamento-cards.component.scss'],
})
export class FormaPagamentoCardsComponent implements OnChanges {

  // ===== INPUTS =====

  /** Moeda padrao da empresa logada (codigo ISO). Controla a matriz. */
  @Input() moedaPadrao: 'PYG' | 'BRL' | 'USD' | 'ARS' | undefined;

  /**
   * Moeda padrao da EMPRESA (codigo ISO), opcional. Quando informada, aplica
   * um filtro adicional sobre os cards visiveis: a lista exibida passa a ser
   * a intersecao entre MOEDAS_COMPATIVEIS[moedaPadrao] (matriz do registro)
   * e MOEDAS_COMPATIVEIS[moedaEmpresa] (matriz da empresa).
   *
   * Usado em telas onde a moeda do registro pode diverguir da moeda da
   * empresa (ex.: gasto em Guarani feito numa empresa configurada em Real)
   * — evita oferecer cards de moedas que a empresa nao opera.
   *
   * Quando omitido, mantem o comportamento historico (apenas matriz do
   * registro). Consumidores que ja passam `moedaPadrao = empresa.moeda`
   * nao precisam preencher este input (interseccao seria idempotente).
   */
  @Input() moedaEmpresa: 'PYG' | 'BRL' | 'USD' | 'ARS' | undefined;

  /** Cotacao USD->PYG (1 USD = N PYG). Carregada pelo consumidor. */
  @Input() cotPYG: number = 0;
  /** Cotacao USD->BRL (1 USD = N BRL). */
  @Input() cotBRL: number = 0;
  /** Cotacao ARS->PYG (1 ARS = N PYG). 0 desabilita o card de Peso. */
  @Input() cotPesoGuarani: number = 0;
  /** Cotacao direta BRL->PYG (1 BRL = N PYG). 0 = usar triangulacao via USD. */
  @Input() cotRealGuarani: number = 0;

  /** Total a pagar na moeda padrao do consumidor. Usado para troco e prefill. */
  @Input() totalAPagar: number = 0;

  /** Valores atuais (banding bidirecional via `[(valores)]`). */
  @Input() valores: ValoresRecebidos = {
    valorRecebidoEmGuarani: 0,
    valorRecebidoEmReal: 0,
    valorRecebidoEmDolar: 0,
    valorRecebidoEmPeso: 0,
  };

  /** Pre-preenche o card da moeda padrao com `totalAPagar` ao abrir. */
  @Input() prefillMoedaPadrao: boolean = false;

  /** Mostra inputs de troco abaixo dos cards (PDV/dialog). */
  @Input() mostrarTroco: boolean = true;

  /** Modo somente leitura. */
  @Input() disabled: boolean = false;

  // ===== OUTPUTS =====

  /** Two-way: `[(valores)]`. Emitido a cada digitacao. */
  @Output() valoresChange = new EventEmitter<ValoresRecebidos>();

  /** Total recebido convertido para moeda padrao do consumidor. */
  @Output() totalConvertidoChange = new EventEmitter<number>();

  /**
   * Trocos por moeda emitidos sempre que `this.troco*` e mutado internamente
   * (digitacao do usuario ou recalculo automatico multi-moeda). O consumidor
   * (dialog/inline) precisa desses valores para subtrair do `valorRecebido*`
   * e gerar `valorLiquido*` ao construir o DTO de pagamento.
   */
  @Output() trocosChange = new EventEmitter<{
    trocoGuarani: number;
    trocoDolar: number;
    trocoReal: number;
    trocoPeso: number;
  }>();

  // ===== ESTADO INTERNO =====

  /** Card destacado visualmente (foco). */
  selectedMoneyCode: 'PYG' | 'USD' | 'BRL' | 'ARS' = 'BRL';

  // Strings formatadas (espelho dos valores numericos para display nos inputs)
  valorGuaraniStr: string = '';
  valorDolarStr: string = '';
  valorRealStr: string = '';
  valorPesoStr: string = '';

  // Troco - valores numericos
  trocoGuarani: number = 0;
  trocoDolar: number = 0;
  trocoReal: number = 0;
  trocoPeso: number = 0;

  // Troco - strings formatadas
  trocoGuaraniStr: string = '';
  trocoDolarStr: string = '';
  trocoRealStr: string = '';
  trocoPesoStr: string = '';

  /** Digits para PYG. */
  digitsMoedaPYG: string = '1.0-0';

  // ViewChilds para foco programatico (API exposta via focusMoeda())
  @ViewChild('inputGuarani') inputGuarani?: ElementRef<HTMLInputElement>;
  @ViewChild('inputDolar') inputDolar?: ElementRef<HTMLInputElement>;
  @ViewChild('inputReal') inputReal?: ElementRef<HTMLInputElement>;
  @ViewChild('inputPeso') inputPeso?: ElementRef<HTMLInputElement>;

  /**
   * Foca o input de uma moeda especifica. Usado pelo consumidor (dialog) para
   * direcionar o foco apos abrir o popup ou trocar de aba.
   */
  focusMoeda(code: 'PYG' | 'USD' | 'BRL' | 'ARS'): void {
    setTimeout(() => {
      if (code === 'PYG') this.inputGuarani?.nativeElement?.focus();
      else if (code === 'USD') this.inputDolar?.nativeElement?.focus();
      else if (code === 'ARS') this.inputPeso?.nativeElement?.focus();
      else this.inputReal?.nativeElement?.focus();
    }, 0);
  }

  /**
   * Forca prefill da moeda padrao com `totalAPagar`. Util quando o consumidor
   * acabou de zerar `valores` e quer pre-preencher o card padrao mesmo que o
   * `prefillMoedaPadrao` ja tenha disparado em ciclo anterior. Idempotente:
   * o `prefillMoedaPadraoCard` interno checa `todosValoresZerados()`.
   */
  prefill(): void {
    if (this.totalAPagar > 0 && this.todosValoresZerados()) {
      this.prefillMoedaPadraoCard();
    }
  }

  /**
   * Reseta valores e strings sem emitir nenhum evento.
   * Usado pelo consumidor quando precisa zerar tudo (ex.: ao abrir o dialog).
   */
  reset(): void {
    this.valores = {
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmPeso: 0,
    };
    this.valorGuaraniStr = '';
    this.valorDolarStr = '';
    this.valorRealStr = '';
    this.valorPesoStr = '';
    this.trocoGuarani = 0;
    this.trocoDolar = 0;
    this.trocoReal = 0;
    this.trocoPeso = 0;
    this.trocoGuaraniStr = '';
    this.trocoDolarStr = '';
    this.trocoRealStr = '';
    this.trocoPesoStr = '';
  }

  // Mapa de conversao ISO <-> enum
  private readonly moedaIsoParaEnum: Record<string, string> = {
    'PYG': 'GUARANI',
    'BRL': 'REAL',
    'USD': 'DOLAR',
    'ARS': 'PESO',
  };
  private readonly moedaEnumParaIso: Record<string, 'PYG' | 'BRL' | 'USD' | 'ARS'> = {
    [Moeda.GUARANI]: 'PYG',
    [Moeda.REAL]:    'BRL',
    [Moeda.DOLAR]:   'USD',
    [Moeda.PESO]:    'ARS',
  };

  // ===== LIFECYCLE =====

  ngOnChanges(changes: SimpleChanges): void {
    // Quando os valores mudam externamente (ex.: pai zera ou injeta valor),
    // sincronizar as strings formatadas e recalcular trocos derivados.
    if (changes['valores']) {
      this.sincronizarStringsComValores();
    }

    // Prefill: ao receber/atualizar `totalAPagar` com `prefillMoedaPadrao` ativo,
    // preenche o card da moeda padrao com o total.
    if (
      this.prefillMoedaPadrao &&
      (changes['totalAPagar'] || changes['moedaPadrao'] || changes['prefillMoedaPadrao']) &&
      this.totalAPagar > 0 &&
      this.todosValoresZerados()
    ) {
      this.prefillMoedaPadraoCard();
    }

    // Reemitir total convertido quando cotacoes ou totalAPagar mudam.
    // Modelo portado do `vendas-app` (Flutter): cada moeda é independente;
    // o operador edita o troco manualmente em qualquer card, sem auto-cálculo
    // distribuído. Ver `docs/ai/PLANO_PORT_TROCO_MOBILE_PARA_WEB.md` (Mudança 2).
    if (changes['cotPYG'] || changes['cotBRL'] || changes['cotPesoGuarani'] || changes['cotRealGuarani'] || changes['totalAPagar']) {
      this.emitirTotalConvertido();
    }
  }

  // ===== GATING (matriz + defesa em profundidade) =====

  /** Card ARS adicionalmente gated por cotPesoGuarani > 0. */
  get pesoHabilitado(): boolean {
    return (this.cotPesoGuarani || 0) > 0;
  }

  /**
   * Conjunto de moedas (codigos ISO) que devem ter card visivel.
   *
   * Regra:
   *  - Sem `moedaEmpresa`: apenas a matriz canonica MOEDAS_COMPATIVEIS para
   *    `moedaPadrao` (comportamento historico).
   *  - Com `moedaEmpresa`: intersecao entre MOEDAS_COMPATIVEIS[moedaPadrao] e
   *    MOEDAS_COMPATIVEIS[moedaEmpresa] (aplica simultaneamente as duas
   *    restricoes de pareamento).
   *
   * Edge case: `moedaPadrao` indefinida ou nao mapeavel -> Set(['PYG'])
   * (fallback conservador, alinhado com a moeda historica do sistema).
   *
   * O card ARS ainda depende de `pesoHabilitado` (defesa em profundidade) —
   * combinacao aplicada no template via *ngIf="moedasVisiveis.has('ARS')
   * && pesoHabilitado".
   */
  get moedasVisiveis(): Set<'PYG' | 'BRL' | 'USD' | 'ARS'> {
    const iso = this.moedaPadrao;
    if (!iso) return new Set(['PYG']);

    const enumDaPadrao = this.moedaIsoParaEnum[iso];
    if (!enumDaPadrao) return new Set(['PYG']);

    // Sem moeda da empresa: comportamento historico (matriz do registro).
    // Com moeda da empresa: intersecao das duas matrizes.
    const enumDaEmpresa = this.moedaEmpresa ? this.moedaIsoParaEnum[this.moedaEmpresa] : undefined;
    const compativeisEnum = enumDaEmpresa
      ? moedasCompativeisInterseccao(enumDaPadrao, enumDaEmpresa)
      : moedasCompativeis(enumDaPadrao);

    if (compativeisEnum.length === 0) return new Set(['PYG']);
    const compativeisIso = compativeisEnum
      .map(m => this.moedaEnumParaIso[m])
      .filter((x): x is 'PYG' | 'BRL' | 'USD' | 'ARS' => !!x);
    return new Set(compativeisIso);
  }

  // ===== REGRA DE ANCORA DINAMICA (R1-R6) =====
  //
  // Ver doc: vendas-site/docs/ai/REGRA_ANCORA_MOEDA_FORMA_PAGAMENTO.md
  //
  // O header de cada card exibe "quanto vale 1 unidade da moeda do card,
  // expresso na moeda da venda (ancora)". O simbolo do numero acompanha a
  // moeda da ancora (R4), nao a moeda do card. O card-ancora exibe `1`.

  /** Ordem de fallback dos cards quando NAO ha uma `moedaPadrao` definida. */
  private static readonly ORDEM_PADRAO: ReadonlyArray<MoedaCodigo> =
    ['PYG', 'USD', 'BRL', 'ARS'];

  /** Locale por moeda — usado pelo CurrencyPipe via pipe `moedaSimbolo`. */
  private static readonly LOCALE_POR_MOEDA: Readonly<Record<MoedaCodigo, string>> = {
    PYG: 'es-PY',
    USD: 'en-US',
    BRL: 'pt-BR',
    ARS: 'es-AR',
  };

  /**
   * Formato de digitos da TAXA de cambio exibida no header do card, por moeda.
   *
   * IMPORTANTE: a taxa do header e uma COTACAO de cambio, nao um valor
   * monetario. Por isso o card PYG usa `1.0-2` (formato adaptativo: 0 a 2
   * casas) — e NAO `1.0-0` como um valor em guaranis. Motivo: o card PYG pode
   * exibir `cotPesoGuarani` (~3,95, fracionario) no caminho venda ARS -> card
   * PYG; com `1.0-0` isso arredondaria para `Gs. 4`. Com `1.0-2`, taxas
   * inteiras (dolarGuarani ~6080, realGuarani ~1170, ancora = 1) saem limpas
   * sem `,00`, e taxas fracionarias (pesoGuarani) preservam os decimais.
   *
   * USD/BRL/ARS usam `1.2-2` (2 casas fixas). Para VALORES monetarios em PYG
   * (faltante/troco) use `digitsMoedaPYG = '1.0-0'` — guarani como dinheiro
   * nao tem centavos; esta constante e so para a taxa do header.
   */
  private static readonly DIGITS_POR_MOEDA: Readonly<Record<MoedaCodigo, string>> = {
    PYG: '1.0-2',
    USD: '1.2-2',
    BRL: '1.2-2',
    ARS: '1.2-2',
  };

  /**
   * Matriz literal de taxa exibida no header (secao 3 do doc).
   *
   * Linhas = `moedaDaVenda` (ancora). Colunas = moeda do card. Cada celula
   * devolve o numero a exibir no header daquele card, **na unidade da ancora**:
   *
   *  - `'1'`: card e a propria ancora (R2).
   *  - `'<campo>'`: usar o valor da cotacao correspondente (dolarGuarani,
   *    realGuarani, dolarReal, pesoGuarani).
   *  - `null`: combinacao nao suportada — card e ocultado (R5/R6).
   *
   * Atencao: o numero e exibido **literalmente** (sem inverter), seguindo a
   * matriz do doc. O SIMBOLO segue a moeda do proprio card (R4, paridade
   * com o mobile). Ex.: Venda USD x Card PYG -> usa `dolarGuarani` (6080)
   * com simbolo `Gs` (do card PYG) -> "Gs 6.080". A interpretacao economica
   * e a do vendas-app Flutter de referencia.
   */
  private static readonly MATRIZ_TAXA: Readonly<
    Record<MoedaCodigo, Readonly<Record<MoedaCodigo, 'ancora' | 'cotPYG' | 'cotBRL' | 'cotRealGuarani' | 'cotPesoGuarani' | null>>>
  > = {
    PYG: { PYG: 'ancora', USD: 'cotPYG',          BRL: 'cotRealGuarani', ARS: 'cotPesoGuarani' },
    USD: { PYG: 'cotPYG', USD: 'ancora',          BRL: 'cotBRL',         ARS: null },
    BRL: { PYG: 'cotRealGuarani', USD: 'cotBRL',  BRL: 'ancora',         ARS: null },
    ARS: { PYG: 'cotPesoGuarani', USD: null,      BRL: null,             ARS: 'ancora' },
  };

  /**
   * Lista ordenada e filtrada de cards a renderizar, com a configuracao
   * de header (taxa, simbolo, formato) pronta para o template.
   *
   * Ordem (R1):
   *  1. Card da `moedaPadrao` (ancora) primeiro.
   *  2. Demais moedas seguindo a ordem fixa PYG, USD, BRL, ARS (excluindo
   *     a ancora ja posicionada).
   *
   * Filtro (R6 + defesa em profundidade):
   *  - Apenas moedas em `moedasVisiveis` (matriz de pareamento PYG/USD/BRL/ARS).
   *  - Card ARS ainda gated por `pesoHabilitado` (cotPesoGuarani > 0).
   *  - Celula `null` na MATRIZ_TAXA (combinacao nao suportada) tambem oculta
   *    o card — invariante redundante com `moedasVisiveis`, mantida como
   *    defesa em profundidade.
   *
   * Fallback: sem `moedaPadrao`, mantem o comportamento historico (apenas
   * PYG visivel via `moedasVisiveis`); o card-ancora degenera para PYG.
   */
  get cardsExibidos(): CardConfig[] {
    const ancora: MoedaCodigo = (this.moedaPadrao ?? 'PYG') as MoedaCodigo;
    const visiveis = this.moedasVisiveis;

    // Ordenacao: ancora primeiro; demais na ordem fixa, excluindo a ancora.
    const ordem: MoedaCodigo[] = [
      ancora,
      ...FormaPagamentoCardsComponent.ORDEM_PADRAO.filter(m => m !== ancora),
    ];

    return ordem
      .filter(m => visiveis.has(m))
      .filter(m => !(m === 'ARS' && !this.pesoHabilitado))
      .map(m => this.montarCardConfig(ancora, m))
      .filter((c): c is CardConfig => c !== null);
  }

  /** Resolve uma celula da MATRIZ_TAXA em um numero concreto. */
  private resolverTaxa(celula: 'ancora' | 'cotPYG' | 'cotBRL' | 'cotRealGuarani' | 'cotPesoGuarani' | null): number | null {
    if (celula === null) return null;
    if (celula === 'ancora') return 1;
    if (celula === 'cotPYG') return this.cotPYG;
    if (celula === 'cotBRL') return this.cotBRL;
    if (celula === 'cotRealGuarani') return this.cotRealGuarani;
    if (celula === 'cotPesoGuarani') return this.cotPesoGuarani;
    return null;
  }

  /** Monta a configuracao do header de um card. Retorna `null` se combinacao invalida. */
  private montarCardConfig(ancora: MoedaCodigo, cardMoeda: MoedaCodigo): CardConfig | null {
    const linha = FormaPagamentoCardsComponent.MATRIZ_TAXA[ancora];
    if (!linha) return null;
    const celula = linha[cardMoeda];
    const taxa = this.resolverTaxa(celula);
    if (taxa === null) return null;

    return {
      moeda: cardMoeda,
      ehAncora: cardMoeda === ancora,
      taxaNumero: taxa,
      taxaSimbolo: cardMoeda,
      taxaDigits: FormaPagamentoCardsComponent.DIGITS_POR_MOEDA[cardMoeda],
      taxaLocale: FormaPagamentoCardsComponent.LOCALE_POR_MOEDA[cardMoeda],
    };
  }

  /** Lookup helper usado pelo template para escolher o ng-template do card. */
  trackByMoeda(_index: number, card: CardConfig): string {
    return card.moeda;
  }

  // ===== INPUT HANDLERS =====

  setSelectedMoney(code: 'PYG' | 'USD' | 'BRL' | 'ARS'): void {
    this.selectedMoneyCode = code;
  }

  /** Conta quantas moedas tem valor > 0 preenchido pelo usuario. */
  get moedasComValor(): number {
    let count = 0;
    if (this.valores.valorRecebidoEmReal > 0) count++;
    if (this.valores.valorRecebidoEmGuarani > 0) count++;
    if (this.valores.valorRecebidoEmDolar > 0) count++;
    if (this.valores.valorRecebidoEmPeso > 0) count++;
    return count;
  }

  onMoneyInput(moeda: 'PYG' | 'USD' | 'BRL' | 'ARS', ev: Event): void {
    if (this.disabled) return;
    const input = ev.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');

    if (moeda === 'PYG') {
      const intVal = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      this.valores = { ...this.valores, valorRecebidoEmGuarani: intVal };
      this.valorGuaraniStr = intVal
        ? intVal.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : '';
      input.value = this.valorGuaraniStr;
    } else {
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / 100;
      if (moeda === 'USD') {
        this.valores = { ...this.valores, valorRecebidoEmDolar: val };
        this.valorDolarStr = cents
          ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
          : '';
        input.value = this.valorDolarStr;
      } else if (moeda === 'BRL') {
        this.valores = { ...this.valores, valorRecebidoEmReal: val };
        this.valorRealStr = cents
          ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
          : '';
        input.value = this.valorRealStr;
      } else {
        this.valores = { ...this.valores, valorRecebidoEmPeso: val };
        this.valorPesoStr = cents
          ? this.formatarPesoArgentino(val)
          : '';
        input.value = this.valorPesoStr;
      }
    }

    this.valoresChange.emit(this.valores);
    this.emitirTotalConvertido();
  }

  onTrocoInput(moeda: 'PYG' | 'USD' | 'BRL' | 'ARS', ev: Event): void {
    if (this.disabled) return;
    const input = ev.target as HTMLInputElement;
    const raw = input.value || '';
    const onlyDigits = raw.replace(/\D+/g, '');

    if (moeda === 'PYG') {
      const intVal = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      this.trocoGuarani = intVal;
      this.trocoGuaraniStr = intVal
        ? intVal.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : '';
      input.value = this.trocoGuaraniStr;
    } else {
      const cents = onlyDigits ? parseInt(onlyDigits, 10) : 0;
      const val = cents / 100;
      if (moeda === 'USD') {
        this.trocoDolar = val;
        this.trocoDolarStr = cents
          ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
          : '';
        input.value = this.trocoDolarStr;
      } else if (moeda === 'BRL') {
        this.trocoReal = val;
        this.trocoRealStr = cents
          ? val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
          : '';
        input.value = this.trocoRealStr;
      } else {
        this.trocoPeso = val;
        this.trocoPesoStr = cents
          ? this.formatarPesoArgentino(val)
          : '';
        input.value = this.trocoPesoStr;
      }
    }

    this.emitirTrocos();
  }

  // ===== CALCULOS DE CONVERSAO =====

  /**
   * Formata valor em Peso Argentino com simbolo `$` (es-AR).
   * Ver pipe `MoedaSimboloPipe` (decisao tech lead 2026-05-06).
   */
  private formatarPesoArgentino(valor: number): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor ?? 0);
  }

  /**
   * Monta as cotacoes numericas para a conversao direta a partir dos inputs
   * do componente. Espelha os campos do backend (`ConversorMoedasUtil.kt`):
   *  - cotPYG ≡ dolarGuarani | cotBRL ≡ dolarReal
   *  - cotRealGuarani ≡ realGuarani | cotPesoGuarani ≡ pesoGuarani
   */
  private get cotacoes(): CotacoesDiretas {
    return {
      cotPYG: this.cotPYG || 0,
      cotBRL: this.cotBRL || 0,
      cotRealGuarani: this.cotRealGuarani || 0,
      cotPesoGuarani: this.cotPesoGuarani || 0,
    };
  }

  /** Moeda-base do consumidor (codigo ISO). Fallback historico: BRL. */
  private get moedaBase(): 'PYG' | 'USD' | 'BRL' | 'ARS' {
    return (this.moedaPadrao || 'BRL') as 'PYG' | 'USD' | 'BRL' | 'ARS';
  }

  /**
   * Converte com seguranca uma parcela de `origem` para `destino` pelo modelo
   * direto. Antes de converter, valida o pareamento via `podeConverterEntre`
   * (espelha `MoedaPareamentoValidator` do backend). Se o par for incompativel
   * (ARS↔USD/BRL) -> registra `console.warn` e devolve 0, NUNCA deixa o erro
   * `ConversaoNaoPermitidaError` propagar (getters chamados no template nao
   * tem try/catch — uma excecao derrubaria a renderizacao).
   *
   * Defesa em profundidade: a matriz `MOEDAS_COMPATIVEIS` ja oculta o card
   * incompativel, logo a parcela de moeda incompativel deveria ser sempre 0.
   */
  private converterParcela(
    valor: number,
    origem: 'PYG' | 'USD' | 'BRL' | 'ARS',
    destino: 'PYG' | 'USD' | 'BRL' | 'ARS',
  ): number {
    if (!valor) return 0;
    if (origem === destino) return valor;

    const origemEnum = this.moedaIsoParaEnum[origem];
    const destinoEnum = this.moedaIsoParaEnum[destino];
    if (!podeConverterEntre(origemEnum, destinoEnum)) {
      console.warn(
        `[forma-pagamento-cards] conversao nao permitida ignorada: ${origem}->${destino} (parcela tratada como 0)`,
      );
      return 0;
    }

    return converterDireto(valor, origem, destino, this.cotacoes);
  }

  /** `totalAPagar` (ja na moeda-base) convertido para a moeda `m`. */
  private totalAPagarEmMoeda(m: 'USD' | 'PYG' | 'BRL' | 'ARS'): number {
    return this.converterParcela(this.totalAPagar, this.moedaBase, m);
  }

  /**
   * Soma de todos os valores recebidos, convertidos diretamente para a moeda
   * `m` (espelha `converterValoresParaMoedaEmpresa` do backend: converte cada
   * parcela direto para a moeda de destino e soma).
   */
  private totalPagoEm(m: 'USD' | 'PYG' | 'BRL' | 'ARS'): number {
    return this.converterParcela(this.valores.valorRecebidoEmDolar || 0, 'USD', m)
      + this.converterParcela(this.valores.valorRecebidoEmGuarani || 0, 'PYG', m)
      + this.converterParcela(this.valores.valorRecebidoEmReal || 0, 'BRL', m)
      + this.converterParcela(this.valores.valorRecebidoEmPeso || 0, 'ARS', m);
  }

  /**
   * Faltante na moeda-base considerando o troco digitado pelo operador.
   *
   * Paridade com o mobile (`payment_by_cash.dart` + `calculateTotalEmMoedaPadrao`
   * em `quotation_generic_controller.dart`): cada card mostra "total liquido"
   * (recebido - troco) por moeda, ao inves do bruto. Sem subtrair o troco,
   * o header exibiria valor diferente do sub-label "Falta informar"
   * (que usa `faltanteParaFechar`, ja correto).
   *
   * Opera direto na moeda-base (nao mais via pivo USD).
   */
  private remainingNaBase(): number {
    const totalTrocoBase = this.calcularTotalTrocoEmMoedaBase();
    const pagoLiquido = this.calcularTotalRecebidoEmMoedaBase() - totalTrocoBase;
    const r = this.totalAPagar - pagoLiquido;
    return r > 0 ? r : 0;
  }

  /** Faltante a pagar exibido no card de cada moeda. */
  remainingEm(moeda: 'USD' | 'PYG' | 'BRL' | 'ARS'): string {
    const rem = this.converterParcela(this.remainingNaBase(), this.moedaBase, moeda);
    if (moeda === 'USD') {
      return rem.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
    } else if (moeda === 'PYG') {
      return rem.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else if (moeda === 'BRL') {
      return rem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      return this.formatarPesoArgentino(rem);
    }
  }

  /** Total recebido convertido para a moeda padrao do consumidor. */
  calcularTotalConvertido(): number {
    return this.totalPagoEm(this.moedaBase);
  }

  // ===== TROCO =====

  calcularTroco(moedaTexto: 'GUARANI' | 'DOLAR' | 'REAL' | 'PESO'): number {
    const map: Record<string, 'USD' | 'PYG' | 'BRL' | 'ARS'> = {
      'DOLAR': 'USD',
      'GUARANI': 'PYG',
      'REAL': 'BRL',
      'PESO': 'ARS',
    };
    const m = map[moedaTexto] || 'USD';
    const troco = this.totalPagoEm(m) - this.totalAPagarEmMoeda(m);
    return troco > 0 ? troco : 0;
  }

  // ===== FALTANTE GLOBAL (UX espelhada do dialog homologacao 8cb476d) =====
  //
  // Os getters/metodos abaixo permitem ao template diferenciar legendas:
  //   - "Falta informar" (cliente ainda nao pagou tudo)
  //   - "Troco devido"   (cliente pagou demais e operador deve dar troco)
  // Ambos calculados em moeda padrao da empresa, depois convertidos para a
  // moeda do card. O sinal preserva a direcao do desbalanco.

  /** Numero de casas decimais da moeda padrao do consumidor. PYG=0, demais=2. */
  private casasDecimaisDaMoeda(): number {
    return (this.moedaPadrao || 'BRL') === 'PYG' ? 0 : 2;
  }

  /**
   * Total recebido: cada parcela convertida diretamente para a moeda-base e
   * somada (espelha `converterValoresParaMoedaEmpresa` do backend).
   */
  private calcularTotalRecebidoEmMoedaBase(): number {
    const base = this.moedaBase;
    return this.converterParcela(this.valores.valorRecebidoEmGuarani || 0, 'PYG', base)
      + this.converterParcela(this.valores.valorRecebidoEmDolar || 0, 'USD', base)
      + this.converterParcela(this.valores.valorRecebidoEmReal || 0, 'BRL', base)
      + this.converterParcela(this.valores.valorRecebidoEmPeso || 0, 'ARS', base);
  }

  /**
   * Soma dos trocos digitados em cada moeda, cada um convertido diretamente
   * para a moeda-base.
   */
  private calcularTotalTrocoEmMoedaBase(): number {
    const base = this.moedaBase;
    return this.converterParcela(this.trocoGuarani || 0, 'PYG', base)
      + this.converterParcela(this.trocoDolar || 0, 'USD', base)
      + this.converterParcela(this.trocoReal || 0, 'BRL', base)
      + this.converterParcela(this.trocoPeso || 0, 'ARS', base);
  }

  /**
   * Indica se o usuario ja interagiu com algum card (valor > 0 em qualquer
   * moeda OU algum troco > 0 digitado). Impede que as legendas apareçam no
   * estado inicial (todos os campos zerados).
   */
  get houveInteracao(): boolean {
    return this.moedasComValor > 0
      || (this.trocoGuarani || 0) > 0
      || (this.trocoDolar || 0) > 0
      || (this.trocoReal || 0) > 0
      || (this.trocoPeso || 0) > 0;
  }

  /**
   * Faltante GLOBAL com sinal, expresso na unidade da moeda pedida.
   *   resultado > 0 → cliente ainda nao pagou tudo (falta informar pagamento)
   *   resultado < 0 → cliente pagou demais (operador deve dar troco)
   *   resultado == 0 → pagamento fechado
   *
   * Critério de "fechado": arredondar pago líquido e total a pagar para a
   * menor unidade da moeda padrão e comparar igualdade exata (modelo do
   * `vendas-app` Flutter). Evita "Falta informar Gs. 3" fantasma originado
   * de resíduos sub-unitários de conversão multi-moeda.
   */
  faltanteParaFechar(moedaTexto: 'GUARANI' | 'DOLAR' | 'REAL' | 'PESO'): number {
    const base = this.moedaBase;
    const totalRecebido = this.calcularTotalRecebidoEmMoedaBase();
    const totalTroco = this.calcularTotalTrocoEmMoedaBase();
    const pagoLiquidoArredondado = arredondarParaMoeda(totalRecebido - totalTroco, base);
    const apagarArredondado = arredondarParaMoeda(this.totalAPagar, base);

    if (pagoLiquidoArredondado === apagarArredondado) {
      return 0;
    }

    const faltanteNaMoedaBase = apagarArredondado - pagoLiquidoArredondado;
    const sinal = faltanteNaMoedaBase > 0 ? 1 : -1;
    const map: Record<string, 'USD' | 'PYG' | 'BRL' | 'ARS'> = {
      'DOLAR': 'USD',
      'GUARANI': 'PYG',
      'REAL': 'BRL',
      'PESO': 'ARS',
    };
    const moedaAlvo = map[moedaTexto] || 'BRL';
    const faltanteAbsolutoNaMoedaAlvo = this.converterParcela(
      Math.abs(faltanteNaMoedaBase), base, moedaAlvo,
    );
    return sinal * faltanteAbsolutoNaMoedaAlvo;
  }

  /**
   * Classifica o desbalanco do pagamento atual (independente de moeda).
   *   'pagamento' → pago liquido < total
   *   'troco'     → pago liquido > total
   *   'nenhum'    → fechado (arredondamentos iguais na moeda base)
   */
  get tipoDeFaltante(): 'pagamento' | 'troco' | 'nenhum' {
    const moedaBase = (this.moedaPadrao || 'BRL') as 'PYG' | 'USD' | 'BRL' | 'ARS';
    const totalRecebido = this.calcularTotalRecebidoEmMoedaBase();
    const totalTroco = this.calcularTotalTrocoEmMoedaBase();
    const pagoLiquidoArredondado = arredondarParaMoeda(totalRecebido - totalTroco, moedaBase);
    const apagarArredondado = arredondarParaMoeda(this.totalAPagar, moedaBase);

    if (pagoLiquidoArredondado === apagarArredondado) {
      return 'nenhum';
    }
    return apagarArredondado > pagoLiquidoArredondado ? 'pagamento' : 'troco';
  }

  /**
   * Decide se a legenda de faltante deve aparecer para a moeda indicada.
   * Suprime a legenda quando o valor convertido fica abaixo da menor unidade
   * (ex: <0,01 BRL/USD/ARS, <1 PYG) — evita "Troco devido: R$ 0,00".
   */
  deveExibirFaltantePara(moedaTexto: 'GUARANI' | 'DOLAR' | 'REAL' | 'PESO'): boolean {
    if (!this.houveInteracao) return false;
    if (this.tipoDeFaltante === 'nenhum') return false;

    const faltante = this.faltanteParaFechar(moedaTexto);
    const absFaltante = Math.abs(faltante);

    const menorUnidade: Record<string, number> = {
      'GUARANI': 1,
      'DOLAR': 0.01,
      'REAL': 0.01,
      'PESO': 0.01,
    };
    return absFaltante >= (menorUnidade[moedaTexto] ?? 0.01);
  }

  // ===== HELPERS =====

  /** Verifica se todos os 4 campos de `valores` estao zerados. */
  private todosValoresZerados(): boolean {
    return !this.valores.valorRecebidoEmGuarani
      && !this.valores.valorRecebidoEmDolar
      && !this.valores.valorRecebidoEmReal
      && !this.valores.valorRecebidoEmPeso;
  }

  /**
   * Re-formata as strings de display a partir dos valores numericos.
   * Usado quando o pai injeta `valores` externamente.
   */
  private sincronizarStringsComValores(): void {
    const v = this.valores;
    this.valorGuaraniStr = v.valorRecebidoEmGuarani
      ? v.valorRecebidoEmGuarani.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : '';
    this.valorDolarStr = v.valorRecebidoEmDolar
      ? v.valorRecebidoEmDolar.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
      : '';
    this.valorRealStr = v.valorRecebidoEmReal
      ? v.valorRecebidoEmReal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
      : '';
    this.valorPesoStr = v.valorRecebidoEmPeso
      ? this.formatarPesoArgentino(v.valorRecebidoEmPeso)
      : '';
  }

  /**
   * Pre-preenche o card da moeda padrao com `totalAPagar`.
   * Idempotente: usa apenas se todos os 4 valores estiverem zerados.
   */
  private prefillMoedaPadraoCard(): void {
    const moedaPadrao = this.moedaPadrao || 'BRL';
    const novoValores: ValoresRecebidos = {
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmPeso: 0,
    };

    if (moedaPadrao === 'PYG') {
      const intVal = Math.round(this.totalAPagar);
      novoValores.valorRecebidoEmGuarani = intVal;
      this.valorGuaraniStr = intVal
        ? intVal.toLocaleString('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0 })
        : '';
      this.selectedMoneyCode = 'PYG';
    } else if (moedaPadrao === 'USD') {
      novoValores.valorRecebidoEmDolar = this.totalAPagar;
      this.valorDolarStr = this.totalAPagar
        ? this.totalAPagar.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
        : '';
      this.selectedMoneyCode = 'USD';
    } else if (moedaPadrao === 'ARS') {
      novoValores.valorRecebidoEmPeso = this.totalAPagar;
      this.valorPesoStr = this.totalAPagar
        ? this.formatarPesoArgentino(this.totalAPagar)
        : '';
      this.selectedMoneyCode = 'ARS';
    } else {
      novoValores.valorRecebidoEmReal = this.totalAPagar;
      this.valorRealStr = this.totalAPagar
        ? this.totalAPagar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })
        : '';
      this.selectedMoneyCode = 'BRL';
    }

    this.valores = novoValores;
    this.valoresChange.emit(this.valores);
    this.emitirTotalConvertido();
  }

  private emitirTotalConvertido(): void {
    this.totalConvertidoChange.emit(this.calcularTotalConvertido());
  }

  /**
   * Emite o snapshot atual dos trocos por moeda para o consumidor.
   * Deve ser chamado DEPOIS de qualquer mutacao em `this.troco*`.
   */
  private emitirTrocos(): void {
    this.trocosChange.emit({
      trocoGuarani: this.trocoGuarani,
      trocoDolar: this.trocoDolar,
      trocoReal: this.trocoReal,
      trocoPeso: this.trocoPeso,
    });
  }
}
